import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { useRefetchQueries, useInvalidateQueries } from '@frontend/api-insurance-detail';
import { useInsuranceTabShallowStore } from '@frontend/insurance-tab';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { SchedulePulseTab, useSchedulePulseShallowStore } from '@frontend/schedule';
import { useAppScopeStore } from '@frontend/scope';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useLocation } from '@frontend/weave-router';
import { useWebsocketEventSubscription } from '@frontend/websocket';

export const useWebsocketInsuranceVerificationEventSubscription = () => {
  const { accessibleLocationIds } = useAppScopeStore();
  const { pathname } = useLocation();

  const { refetchInsuranceStatuses } = useInsuranceTabShallowStore('refetchInsuranceStatuses');
  const { refetchGetInsuranceByPersonIdQuery } = useRefetchQueries();
  const { invalidateCachedDentalCoverageInfoByPersonId, invalidatePatientEligibilityListViewByLocation } =
    useInvalidateQueries();
  const { locationIdWiseCustomizationFlagDetails } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();
  const {
    personId: contactPanelPersonId,
    selectedTab,
    personLocationId,
  } = useContactPanelShallowStore('personId', 'selectedTab', 'personLocationId');

  const { selectedTab: selectedSchedulePulseTab } = useSchedulePulseShallowStore('selectedTab');

  const getIsContactPanelOpenForSameLocationAndPerson = (personId: string, locationId: string) => {
    if (!personId || !locationId) return false;

    const isInsuranceTabOpenInContactPanel = selectedTab === 'insurance-tab';
    const isContactPanelForSamePerson = personId === contactPanelPersonId;
    const isContactPanelForSameLocation = locationId === personLocationId;
    const isContactPanelOpenForSameLocationAndPerson =
      isInsuranceTabOpenInContactPanel && isContactPanelForSamePerson && isContactPanelForSameLocation;

    return isContactPanelOpenForSameLocationAndPerson;
  };

  const getShouldRefreshInsuranceDetails = (personId: string, locationId: string, requestType: string) => {
    const isManualVerificationRequest = requestType === 'ManualVerification';

    if (!personId || !locationId || !isManualVerificationRequest) return false;

    const hasPersonIdAndLocationId = !!personId && !!locationId;
    const isLocationAccessible = hasPersonIdAndLocationId && accessibleLocationIds.includes(locationId || '');
    const isSchedulePulseInsuranceTabOpen = selectedSchedulePulseTab === SchedulePulseTab.Insurance;

    const isContactPanelOpenForSameLocationAndPerson = getIsContactPanelOpenForSameLocationAndPerson(
      personId || '',
      locationId || ''
    );

    const isUserOnValidSection = isSchedulePulseInsuranceTabOpen || isContactPanelOpenForSameLocationAndPerson;

    const isWeaveEnhancedInsuranceVerificationEnabled =
      locationIdWiseCustomizationFlagDetails[locationId || ''] || false;

    const shouldRefreshInsuranceDetails =
      isUserOnValidSection && isLocationAccessible && isWeaveEnhancedInsuranceVerificationEnabled;

    return shouldRefreshInsuranceDetails;
  };

  // Invalidate the get insurance verification details query when an insurance verification notification is received and refresh the insurance statuses
  return useWebsocketEventSubscription(NotificationType.INSURANCE_VERIFICATION, (payload) => {
    const { personId, locationId, requestType } = payload?.params || {};

    // Do not proceed if personId or locationId is missing in the payload
    if (!personId || !locationId) return;

    const isScheduleCalendar = pathname.startsWith('/schedule/calendar');

    // When viewing the eligibility list view, invalidate its query so the row
    // (status + "Last checked"/"Last verified" text) auto-updates once the
    // backend finishes processing the verification. The payload only carries
    // locationId, so we invalidate all list queries for that location.
    const isEligibilityListView = pathname.startsWith('/schedule/eligibility-list');
    if (isEligibilityListView && accessibleLocationIds.includes(locationId as string)) {
      invalidatePatientEligibilityListViewByLocation(locationId as string);
    }

    // Check if we need to refresh insurance details
    const shouldRefreshInsuranceDetails =
      isScheduleCalendar || getShouldRefreshInsuranceDetails(personId, locationId, requestType || '');

    // Only invalidate and refetch if we have both personId and locationId and the location is accessible
    // Also check if the Weave Enhanced Insurance Verification feature is enabled for the location
    if (shouldRefreshInsuranceDetails) {
      refetchGetInsuranceByPersonIdQuery(locationId as string, personId as string);
      refetchInsuranceStatuses?.();
      invalidateCachedDentalCoverageInfoByPersonId(personId as string, locationId as string);
    }
  });
};
